import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { userAPI } from '../../services/api';
import styles from './Header.module.css';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const userInfo = user?.userInfo;

  const handleCheckin = async () => {
    try {
      console.log('Check-in clicked');
      navigate('/checkin');
      setShowDropdown(false);
    } catch (error) {
      console.error('Check-in failed:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowDropdown(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowDropdown(false);
    setShowMobileMenu(false);
  };

  const handleMobileMenuItemClick = (path) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  const getAvatarText = (user) => {
    const name = user?.username || user?.nickname || user?.email || '用户';
    return name.charAt(0).toUpperCase();
  };

  // 获取用户资料信息
  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated()) return;

    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  }, [isAuthenticated]);

  // 获取用户资料
  useEffect(() => {
    if (isAuthenticated()) {
      fetchUserProfile();
    }
  }, [isAuthenticated, fetchUserProfile]);

  // 监听user变化，同步金币余额
  useEffect(() => {
    if (user && user.coinBalance !== undefined) {
      setUserProfile(prev => prev ? { ...prev, coinBalance: user.coinBalance } : null);
    }
  }, [user]);

  // 获取显示的金币余额（优先使用AuthContext中的数据）
  const getCoinBalance = () => {
    return user?.coinBalance !== undefined ? user.coinBalance : (userProfile?.coinBalance || 0);
  };

  // Close dropdown and mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Check if the click is not on the hamburger button
        const hamburgerButton = document.querySelector('[data-mobile-menu-button]');
        if (hamburgerButton && !hamburgerButton.contains(event.target)) {
          setShowMobileMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className={styles.header}>
      <div className="container">
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <Link to="/" className={styles.logo}>
              万盘搜
            </Link>
            <nav className={styles.nav}>
              <Link to="/profile" className={styles.navLink}>个人中心</Link>
              {/* <Link to="/tags" className={styles.navLink}>标签列表</Link> */}
              {/* <Link to="/docs" className={styles.navLink}>文档</Link> */}
            </nav>
          </div>

          <div className={styles.headerRight}>
            <button
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label="切换主题"
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>

            <button 
              className={styles.contactButton} 
              onClick={() => setShowContactModal(true)}
            >
              <span>📧</span> 联系我们
            </button>

            {isAuthenticated() ? (
              <div className={styles.userSection}>
                <div 
                  className={styles.coinDisplay}
                  onClick={() => {
                    navigate('/profile', { state: { tab: 'points' } });
                    setShowDropdown(false);
                    setShowMobileMenu(false);
                  }}
                >
                  <span className={styles.coinIcon}>🪙</span>
                  <span className={styles.coinAmount}>{getCoinBalance()}</span>
                </div>
                <div className={styles.userAvatarMenu} ref={dropdownRef}>
                  <div
                    className={styles.userAvatar}
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    {userInfo?.avatar ? (
                      <img src={userInfo.avatar} alt="用户头像" className={styles.avatarImage} />
                    ) : (
                      <div className={styles.avatarPlaceholder}>
                        {getAvatarText(userInfo)}
                      </div>
                    )}
                  </div>

                {showDropdown && (
                  <div className={styles.dropdownMenu}>
                    <div className={styles.dropdownHeader}>
                      <span className={styles.userName}>{userInfo?.username || userInfo?.email}</span>
                    </div>
                    <div className={styles.dropdownDivider}></div>
                    <button onClick={handleProfileClick} className={styles.dropdownItem}>
                      <span className={styles.dropdownIcon}>👤</span>
                      个人中心
                    </button>
                    <button onClick={handleCheckin} className={styles.dropdownItem}>
                      <span className={styles.dropdownIcon}>📅</span>
                      签到
                    </button>
                    <div className={styles.dropdownDivider}></div>
                    <button onClick={handleLogout} className={`${styles.dropdownItem} ${styles.logout}`}>
                      <span className={styles.dropdownIcon}>🚪</span>
                      退出登录
                    </button>
                  </div>
                )}
                </div>
              </div>
            ) : (
              <div className={styles.authButtons}>
                <Link to="/login" className="btn btn-secondary">
                  登录
                </Link>
                <Link to="/register" className="btn btn-primary">
                  注册
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className={styles.mobileMenuButton}
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              data-mobile-menu-button
              aria-label="菜单"
            >
              <span className={styles.hamburger}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className={styles.mobileMenu} ref={mobileMenuRef}>
          <div className="container">
            <nav className={styles.mobileNav}>
              <button onClick={() => handleMobileMenuItemClick('/profile')} className={styles.mobileNavLink}>
                <span className={styles.mobileNavIcon}>👤</span>
                个人中心
              </button>
              <button onClick={() => handleMobileMenuItemClick('/tags')} className={styles.mobileNavLink}>
                <span className={styles.mobileNavIcon}>🏷️</span>
                标签列表
              </button>
              <button onClick={() => handleMobileMenuItemClick('/docs')} className={styles.mobileNavLink}>
                <span className={styles.mobileNavIcon}>📄</span>
                文档
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      {showContactModal && (
        <div className={styles.modalOverlay} onClick={() => setShowContactModal(false)}>
          <div className={styles.contactModal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>联系我们</h3>
              <button className={styles.closeButton} onClick={() => setShowContactModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.contactItem}>
                <span className={styles.contactLabel}>邮箱:</span>
                <span className={styles.contactValue}>market@aicosmos.cn</span>
              </div>
              <div className={styles.contactItem}>
                <span className={styles.contactLabel}>QQ:</span>
                <span className={styles.contactValue}>642933160</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;